import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, CornerDownLeft, ArrowRight } from 'lucide-react';
import { api } from '@/lib/api';
import { ModuleIcon } from '@/components/ModuleIcon';

const BADGE_CLS = { draft: 'bg-slate-100 text-slate-600', open: 'bg-amber-50 text-amber-700', paid: 'bg-emerald-50 text-emerald-700', running: 'bg-emerald-50 text-emerald-700', won: 'bg-emerald-50 text-emerald-700', lost: 'bg-rose-50 text-rose-700', cancelled: 'bg-rose-50 text-rose-700', active: 'bg-emerald-50 text-emerald-700' };

/** Global search box for the Apps home: searches apps (client side) + all permitted modules (GET /search). */
export const GlobalSearch = ({ company, tiles, lang }) => {
  const id = lang === 'id';
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2 || !company?.id) { setRes(null); setBusy(false); return undefined; }
    setBusy(true);
    const h = setTimeout(async () => {
      try { const { data } = await api.get(`/companies/${company.id}/search`, { params: { q: term } }); setRes(data); } catch (e) { setRes({ groups: [] }); } finally { setBusy(false); }
    }, 250);
    return () => clearTimeout(h);
  }, [q, company?.id]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); inputRef.current?.focus(); }
      else if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) { e.preventDefault(); inputRef.current?.focus(); }
    };
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener('keydown', onKey); document.addEventListener('mousedown', onClick);
    return () => { window.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick); };
  }, []);

  const term = q.trim().toLowerCase();
  const appHits = useMemo(() => (term.length < 1 ? [] : tiles.filter((m) => (m.label_id || '').toLowerCase().includes(term) || (m.label_en || '').toLowerCase().includes(term) || (m.key || '').includes(term)).slice(0, 6)), [tiles, term]);
  const flat = useMemo(() => [
    ...appHits.map((m) => ({ kind: 'app', key: `app-${m.key}`, module: m.key, title: id ? m.label_id : m.label_en, subtitle: id ? 'Buka aplikasi' : 'Open app', path: m.path })),
    ...(res?.groups || []).flatMap((g) => g.items.map((it) => ({ kind: 'item', key: `${g.module}-${it.id}`, module: g.module, group: id ? g.label_id : g.label_en, ...it }))),
  ], [appHits, res, id]);
  useEffect(() => { setCursor(0); }, [flat.length, q]);

  const go = (it) => { if (!it) return; setOpen(false); setQ(''); navigate(it.path); };
  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); go(flat[cursor]); }
    else if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); }
  };
  useEffect(() => { boxRef.current?.querySelector(`[data-idx="${cursor}"]`)?.scrollIntoView({ block: 'nearest' }); }, [cursor]);

  const showPanel = open && term.length >= 1;
  let lastGroup = null;
  return (
    <div ref={boxRef} className="relative mx-auto w-full max-w-2xl" data-testid="global-search">
      <div className={`flex items-center gap-3 rounded-2xl border bg-white px-4 shadow-soft transition-shadow ${open ? 'border-primary/40 shadow-lg ring-4 ring-primary/10' : 'border-border'}`}>
        <Search size={18} className="shrink-0 text-muted-foreground" />
        <input ref={inputRef} value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={onKeyDown}
          placeholder={id ? 'Cari apa saja: kontak, produk, faktur, aset, karyawan, jurnal, pesan…' : 'Search anything: contacts, products, invoices, assets, employees…'}
          className="h-14 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground/80" aria-label="Pencarian global" data-testid="global-search-input" autoComplete="off" />
        {busy ? <Loader2 size={16} className="animate-spin text-muted-foreground" /> : <kbd className="hidden sm:inline-flex h-6 items-center rounded-md border border-border bg-muted px-1.5 font-mono text-[10px] text-muted-foreground">Ctrl K</kbd>}
      </div>
      {showPanel && (
        <div className="absolute left-0 right-0 top-full z-30 mt-2 max-h-[60vh] overflow-y-auto rounded-2xl border border-border bg-white p-2 shadow-xl text-left" data-testid="global-search-results">
          {flat.length === 0 && !busy && term.length >= 2 && <div className="px-3 py-8 text-center text-sm text-muted-foreground" data-testid="global-search-empty">{id ? `Tidak ada hasil untuk “${q.trim()}”` : `No results for “${q.trim()}”`}</div>}
          {flat.length === 0 && term.length < 2 && appHits.length === 0 && <div className="px-3 py-6 text-center text-xs text-muted-foreground">{id ? 'Ketik minimal 2 huruf untuk mencari data…' : 'Type at least 2 characters…'}</div>}
          {flat.map((it, idx) => {
            const header = it.kind === 'app' ? (id ? 'Aplikasi' : 'Apps') : it.group;
            const showHeader = header !== lastGroup; lastGroup = header;
            return (
              <React.Fragment key={it.key}>
                {showHeader && <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{header}</div>}
                <button type="button" data-idx={idx} onMouseEnter={() => setCursor(idx)} onClick={() => go(it)} data-testid={`global-search-result-${it.key}`}
                  className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors ${idx === cursor ? 'bg-primary/5' : 'hover:bg-muted/60'}`}>
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-xl">{it.image ? <img src={it.image} alt="" className="h-full w-full object-cover" /> : <ModuleIcon moduleKey={it.module} size="sm" />}</div>
                  <div className="min-w-0 flex-1"><div className="truncate text-sm font-medium">{it.title}</div>{it.subtitle && <div className="truncate text-xs text-muted-foreground">{it.subtitle}</div>}</div>
                  {it.badge && <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] ${BADGE_CLS[it.badge] || 'bg-muted text-muted-foreground'}`}>{it.badge}</span>}
                  {idx === cursor ? <CornerDownLeft size={14} className="shrink-0 text-muted-foreground" /> : <ArrowRight size={14} className="shrink-0 text-muted-foreground/40" />}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};
